/**
 * ScheduleImpactPreviewModal
 * Generic modal for previewing the impact of contract actions (extension, rollover, capital amendment).
 * Shows summary comparison cards + full projected schedule using AmortizationScheduleTable.
 * Opened from action dialogs via "Preview Impact" button.
 */

import React from 'react';
import { formatNumber } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import AmortizationScheduleTable from './AmortizationScheduleTable';

const ScheduleImpactPreviewModal = ({
  open,
  onClose,
  title = 'Schedule Impact Preview',
  subtitle,
  previewData,
  contract = {},
  contractOverrides = {},
  currencyCode,
}) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  if (!previewData) return null;

  const displayContract = { ...contract, ...contractOverrides };
  const cc = currencyCode || displayContract?.currency_code || '';

  const current = previewData.current || {};
  const projected = previewData.projected || {};
  const interestDiff = previewData.interest_diff || (projected.total_interest - current.total_interest) || 0;
  const isIncrease = interestDiff > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Subtitle / context info */}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}

        {/* Summary Comparison Cards */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Impact Summary</Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current Total Interest</Typography>
                  <Typography variant="h6">{cc} {formatNumber(current.total_interest, dp)}</Typography>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Projected Total Interest</Typography>
                  <Typography variant="h6" color={isIncrease ? 'error.main' : 'success.main'}>
                    {cc} {formatNumber(projected.total_interest, dp)}
                  </Typography>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Box sx={{
                  textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1,
                  backgroundColor: isIncrease ? '#ffebee' : '#e8f5e9',
                }}>
                  <Typography variant="caption" color="text.secondary">Difference</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h6" color={isIncrease ? 'error.main' : 'success.main'}>
                      {isIncrease ? '+' : ''}{cc} {formatNumber(interestDiff, dp)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 3
                }}>
                <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Schedule Entries</Typography>
                  <Typography variant="h6">
                    {current.entries || '-'} → {projected.entries || '-'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Projected Schedule Table */}
        {previewData.projected_schedule && previewData.projected_schedule.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Projected Schedule ({previewData.projected_schedule.length} entries)
            </Typography>
            <AmortizationScheduleTable
              schedule={previewData.projected_schedule}
              contract={displayContract}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleImpactPreviewModal;
